import { create } from 'zustand';
import i18n, { DATE_LOCALE } from '../i18n';

const useDocumentStore = create((set, get) => ({
  // Состояние
  documents: [],
  activeDocumentId: null,
  documentResult: null,
  loading: false,
  error: '',
  file: null,
  
  // Add new state properties
  isPublic: null,
  isPublicLoading: false,
  isFetchingDocumentById: null,
  
  isUploading: false,
  showLimitModal: false,
  remainingDocumentsCount: 3,
  
  // Действия
  setFile: (file) => set({ file }),
  setDocuments: (documents) => set({ documents }),
  setActiveDocumentId: (id) => {
    set({ activeDocumentId: id });
    if (id) {
      window.history.pushState({}, '', `/documents/${id}`);
    } else {
      window.history.pushState({}, '', '/documents');
    }
  },
  setDocumentResult: (result) => set({ 
    documentResult: {
      ...result,
      result: Array.isArray(result?.result) ? result.result : []
    }
  }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setShowLimitModal: (show) => set({ showLimitModal: show }),
  
  // Асинхронные действия
  fetchDocuments: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        set({ documents: data.documents });
        return data.documents;
      } else {
        console.error('Failed to fetch documents');
        return [];
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      return [];
    }
  },
  
  fetchProcessedDocumentsCount: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/remaining-documents-count`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        set({ remainingDocumentsCount: data.count });
        return data.count;
      } else {
        console.error('Failed to fetch remaining documents count');
        return 3;
      }
    } catch (error) {
      console.error('Error fetching remaining documents count:', error);
      return 3;
    }
  },
  
  checkDocumentLimit: () => {
    return get().remainingDocumentsCount <= 0;
  },
  
  uploadDocuments: async (files) => {
    // Если лимит исчерпан, показываем модалку и сбрасываем value у input,
    // чтобы повторное добавление того же файла снова сработало onChange
    if (get().checkDocumentLimit()) {
      set({ showLimitModal: true });

      const fileInput = document.getElementById('fileInput');
      if (fileInput) {
        fileInput.value = '';
      }

      return false;
    }

    set({ 
      loading: true, 
      error: '', 
      isUploading: true,
    });

    const uploadPromises = Array.from(files).map(async (file) => {
      const formData = new FormData();
      formData.append('files', file);

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          return data;
        } else {
          throw new Error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        throw error;
      }
    });

    try {
      const results = await Promise.all(uploadPromises);
      
      // Update documents list with all new documents
      set({ 
        error: '', 
        file: null,
        isUploading: false,
        documents: [
          ...results.map(data => ({
            created_at: data.created_at,
            filename: data.filename,
            id: data.id,
            is_public: data.is_public,
          })),
          ...(get().documents)
        ],
      });

      // Clear file input
      const fileInput = document.getElementById('fileInput');
      if (fileInput) {
        fileInput.value = '';
      }

      // Set the first uploaded document as active
      if (results.length > 0) {
        const firstDoc = results[0][0];
        set({ 
          documentResult: {
            ...firstDoc,
            result: Array.isArray(firstDoc?.result) ? firstDoc.result : []
          },
          activeDocumentId: firstDoc.id,
          loading: true 
        });
        window.history.pushState({}, '', `/documents/${firstDoc.id}`);
        get().pollDocumentStatus(firstDoc.id);
      }

      // Обновляем количество обработанных документов
      await get().fetchProcessedDocumentsCount();

      return true;
    } catch (error) {
      console.error('Error uploading documents:', error);
      set({
        error: i18n.t('errors.uploadDocuments'),
        loading: false,
        isUploading: false
      });
      return false;
    }
  },
  
  deleteDocument: async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const { activeDocumentId, documents } = get();
        
        // Если удаляем текущий документ, найдем следующий или предыдущий
        if (documentId === activeDocumentId) {
          const currentIndex = documents.findIndex(doc => doc.id === documentId);
          let nextDoc = null;

          if (currentIndex > 0) {
            // Если есть документ выше, берем его
            nextDoc = documents[currentIndex - 1];
          } else if (currentIndex === 0 && documents.length > 1) {
            // Если удаляем первый документ и есть следующий
            nextDoc = documents[1];
          }

          if (nextDoc) {
            // Если нашли документ для переключения
            await get().fetchDocuments(); // Обновляем список документов
            get().handleDocumentClick(nextDoc.id);
          } else {
            // Если документов больше нет
            set({ 
              activeDocumentId: null,
              documentResult: null
            });
            window.history.pushState({}, '', '/documents');
            await get().fetchDocuments();
          }
        } else {
          // Если удаляем не текущий документ, просто обновляем список
          await get().fetchDocuments();
        }
        return true;
      } else {
        const data = await response.json();
        set({ error: data.message || i18n.t('errors.deleteDocument') });
        return false;
      }
    } catch (error) {
      console.error('Error:', error);
      set({ error: i18n.t('errors.deleteDocumentGeneric') });
      return false;
    }
  },
  
  pollDocumentStatus: async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        set({ 
          documentResult: data,
          isPublic: data.is_public || false,
        });
        
        // Return status to decide if polling should continue
        if (data.status === 'completed' || data.status === 'error') {
          set({ loading: false });
          return false;
        }
        return true;
      }
    } catch (error) {
      console.error('Error polling document status:', error);
      set({ loading: false, error: i18n.t('errors.documentStatus') });
      return false;
    }
  },
  
  handleDocumentClick: async (documentId) => {
    // Проверяем лимит перед выбором документа
    if (get().checkDocumentLimit()) {
      set({ showLimitModal: true });
      return;
    }

    set({ loading: true, error: '' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        set({ 
          documentResult: {
            ...data,
            result: Array.isArray(data?.result) ? data.result : []
          },
          activeDocumentId: documentId,
          loading: false 
        });
        window.history.pushState({}, '', `/documents/${documentId}`);
      } else {
        throw new Error('Failed to fetch document');
      }
    } catch (error) {
      console.error('Error:', error);
      set({ error: i18n.t('errors.fetchDocument'), loading: false });
    }
  },
  
  // Вспомогательные функции
  formatDate: (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString(DATE_LOCALE, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },
  
  // Add new actions for public access
  enablePublicAccess: async (documentId) => {
    set({ isPublicLoading: true });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/${documentId}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        set({ 
          isPublic: true,
          isPublicLoading: false
        });
        return data;
      } else {
        throw new Error('Failed to enable public access');
      }
    } catch (error) {
      console.error('Error enabling public access:', error);
      set({ isPublicLoading: false });
      throw error;
    }
  },

  disablePublicAccess: async (documentId) => {
    set({ isPublicLoading: true });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents/${documentId}/unshare`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        set({ 
          isPublic: false,
          publicUrl: null,
          isPublicLoading: false
        });
        return await response.json();
      } else {
        throw new Error('Failed to disable public access');
      }
    } catch (error) {
      console.error('Error disabling public access:', error);
      set({ isPublicLoading: false });
      throw error;
    }
  },

  fetchDocumentById: async (documentId) => {
    const { isFetchingDocumentById } = get();
    if (isFetchingDocumentById) {
      return;
    }

    set({ isFetchingDocumentById: true });
    try {
      const token = localStorage.getItem('token');
      let response;
      if (token) {
        response = await fetch(`${process.env.REACT_APP_API_URL}/documents/${documentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } else {
        response = await fetch(`${process.env.REACT_APP_API_URL}/documents/${documentId}`);
      }
      if (response.ok) {
        const data = await response.json();
        set({ 
          activeDocumentId: documentId,
          documentResult: data,
          isPublic: data.is_public || false,
          isFetchingDocumentById: false
        });
        return data.is_public;
      }
      set({ isFetchingDocumentById: false, isPublic: false });
      return false;
    } catch (error) {
      set({ isFetchingDocumentById: false, isPublic: false });
      return false;
    }
  },
}));

export default useDocumentStore; 
